import cx from "classnames";
import React, { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { connect } from "react-redux";
import {
  Route as DefaultRoute,
  RouteProps as DefaultRouteProps,
  Redirect,
  RouteComponentProps,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";

import blueHelpSvg from "assets/img/help-blue.svg";
import About from "components/About";
import EmailAuditLogs from "components/EmailAuditLog";
import ErrorBoundary from "components/ErrorBoundary";
import Header from "components/Header";
import Icon from "components/Icon";
import Link from "components/Link";
import { LogoLink } from "components/Logo";
import ManageAllData from "components/ManageAllData";
import ManageAllDataOld from "components/ManageAllDataOld";
import UploadProgressBar from "components/Progress-Component";
import ManageReports from "components/reports/ManageReports";
import Config, { hasFeature } from "config";
import CreateProject from "containers/CreateProject";
import DirDownloads from "containers/DirDownloads";
import Downloads from "containers/Downloads";
import HomePage from "containers/HomePage";
import Logout from "containers/Logout";
import ManageAnnouncements from "containers/ManageAnnouncements";
import ManageChains from "containers/ManageChains";
import ManageFiles from "containers/ManageFiles";
import ManageMappingData from "containers/ManageMappingData";
import ManageStores from "containers/ManageStores";
import ManageSurveys from "containers/ManageSurveys";
import ManageUsers from "containers/ManageUsers";
import MyAnnouncements from "containers/MyAnnouncements";
import MyFeedback from "containers/MyFeedback";
import MyStores from "containers/MyStores";
import MySurveys from "containers/MySurveys";
import MyWork from "containers/MyWork";
import Navigation from "containers/Navigation";
import NavigationBreadcrumb from "containers/NavigationBreadcrumb";
import NearbyStores from "containers/NearbyStores";
import PageContainer from "containers/PageContainer";
import Profile from "containers/Profile";
import Project from "containers/Project";
import Projects from "containers/Projects";
import ReviewFeedback from "containers/ReviewFeedback";
import SignIn from "containers/SignIn";
import StarredPage from "containers/StarredPage";
import Store from "containers/Store";
import StoreResetFeedback from "containers/StoreResetFeedback";
import StoreResetStatus from "containers/StoreResetStatus";
import Support from "containers/Support";
import UploadAttachments from "containers/UploadAttachments";
import ValidationCompletion from "containers/ValidationCompletion";
import { PermissionsContext } from "context";
import Constants from "helpers/constants";
import { getItem, getItemJson, setItem } from "helpers/storage";
import { useIdleTimer } from "react-idle-timer";
import { Bounce, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { State } from "state";
import { PageName } from "state/analytics";
import { CHAIN_ROLE, IMPERSONATIONUSER } from "state/auth";
import { AuthUser } from "state/auth_user";
import { getUserZipCode } from "state/location";
import { Permission } from "state/permissions";
import { UserRole } from "types/roles";
import SearchBar from "../components/SearchBar";
import "../styles/modules/_toast-container.scss";
import AssociatedChainsPage from "./AssociatedChainsPage";
import CreateProjectNew from "./CreateProjectNew";
import ManageAnnouncementsNew from "./ManageAnnouncementsNew";
import ManageChainParentData from "./ManageChainParentData";
import ManagePSAFiles from "./ManagePSAFiles";
import ManageRequests from "./RequestDashboard/ManageRequests";
import RequestDetails from "./RequestDashboard/RequestDetails";

interface LayoutProps {
  currentRoute?: string;
}

const OldLayout: React.FunctionComponent<LayoutProps> = ({ children, currentRoute }) => {
  const [state, setState] = useState<string>("Active");

  const onIdle = () => {
    setState("Idle");
    window.location.href = "/logout";
  };

  const onActive = () => {
    setState("Active");
  };

  const onAction = () => {};

  useIdleTimer({
    onIdle,
    onActive,
    onAction,
    timeout: parseInt(Config.IDLE_TIMEOUT), // 24 hours -> 24*60*60*1000 -> 86 400 000
    throttle: 500,
    crossTab: true,
  });

  return (
    <section className="grid-container">
      <section className="grid-item logo" data-qa="logo">
        <LogoLink />
      </section>
      <section className="grid-item empty" />
      <section className="grid-item search">
        <SearchBar />
      </section>
      <section className="grid-item support">
        <a
          href="https://reset-iq.zendesk.com/hc/en-us/requests/new?"
          target="_blank"
          rel="noreferrer noopener"
          style={{ color: "#3c6df1" }}
        >
          <Icon src={blueHelpSvg} small={true} />
          <span>Request Support</span>
        </a>
      </section>
      <section className="grid-item support-documents">
        <Link
          to="/support"
          className={cx("header-link", {
            "header-link-active": currentRoute === "/support",
          })}
          data-qa="header-link-support"
        >
          <span>Support Documents</span>
        </Link>
      </section>
      <section className="grid-item profile">
        <Profile />
      </section>
      <section className="grid-item grid-navigation">
        <Navigation />
      </section>
      <section className="grid-item breadcrumb">
        <NavigationBreadcrumb />
      </section>
      <section className="grid-item main">{children}</section>
    </section>
  );
};

const Layout: React.FunctionComponent<LayoutProps> = ({ children, currentRoute }) => {
  const [state, setState] = useState<string>("Active");
  const [impersonatedUser, setImpersonatedUser] = useState<any | null>(null);

  useEffect(() => {
    const syncImpersonation = () => {
      const userStr = getItem(IMPERSONATIONUSER);
      if (!userStr) {
        setImpersonatedUser(null);
        return;
      }
      try {
        const raw2 = JSON.parse(userStr);
        if(raw2){
          const raw =JSON.parse(raw2)
          setImpersonatedUser({
            ...raw,
            first_name:
              raw.first_name ??
              raw.firstname ??
              raw.firstName ??
              raw.user?.first_name ??
              "",
            last_name:
              raw.last_name ??
              raw.lastname ??
              raw.lastName ??
              raw.user?.last_name ??
              "",
          });
        }
      } catch (e) {
        console.error("Failed to parse impersonated user", e);
        setImpersonatedUser(null);
      }
    };
    syncImpersonation();
    window.addEventListener("impersonation-updated", syncImpersonation);
    return () => {
      window.removeEventListener("impersonation-updated", syncImpersonation);
    };
  }, []);

  const onIdle = () => {
    setState("Idle");
    window.location.href = "/logout";
  };

  const onActive = () => {
    setState("Active");
  };

  const onAction = () => {};

  useIdleTimer({
    onIdle,
    onActive,
    onAction,
    timeout: parseInt(Config.IDLE_TIMEOUT), // 24 hours -> 24*60*60*1000 -> 86 400 000
    throttle: 500,
    crossTab: true,
  });

  return (
    <div
      style={{
        height: "100%",
        minHeight: "100vh",
        width: "100%",
        margin: 0,
        padding: 0,
        backgroundColor: "#DFDFDF",
      }}
    >
      <Header currentRoute={currentRoute} impersonateUser={impersonatedUser} />
      <section
        style={{ height: "100%", width: "100%", padding: "20px", backgroundColor: "#DFDFDF" }}
      >
        {children}
      </section>
      <UploadProgressBar />
    </div>
  );
};

export interface RouteProps extends DefaultRouteProps {
  pageName: PageName;
  match?: any;
  location?: any;
  history?: any;
}

const Route: React.FunctionComponent<RouteProps> = ({ pageName, ...props }) => {
  // props.location should always be present for top-level pages
  // usually pages using withRouter
  if (props.location) {
    props.location.state = { pageName };
    if (pageName !== "Sign In" && pageName !== "Sign Out") {
      sessionStorage.setItem("redirectedPath", props.location.pathname);
    }
  }

  return <DefaultRoute {...props} />;
};

export interface PrivateRouteProps extends RouteProps {
  isAuthenticated: boolean;
  allowedRoles: UserRole[];
  role?: UserRole;
  requiredUserLevelPermissions?: any[];
  userLevelPermissions?: any[];
  component: React.ComponentType<any>;
  allowProjectAdmin?: boolean;
}

const PrivateRoute: React.FunctionComponent<PrivateRouteProps> = ({
  isAuthenticated = false,
  allowedRoles = [],
  role,
  requiredUserLevelPermissions = [],
  userLevelPermissions = [],
  component: Component,
  allowProjectAdmin = false,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props: RouteComponentProps) => {
        const chainPermissionString = getItem(CHAIN_ROLE);
        const chainPermission = JSON.parse(chainPermissionString || "[]");
        const permission = chainPermission;
        const roles = Array.isArray(permission) ? permission : [permission];

        const isProjectAdmin = roles.some(
          (r: any) =>
            r.role_name === "Internal Project Admin" || r.role_name === "External Project Admin"
        );

        // Only bypass for project admin if route explicitly allows it
        if (isProjectAdmin && allowProjectAdmin) {
          return <Component {...props} />;
        }
        if (isAuthenticated) {
          if (requiredUserLevelPermissions.length) {
            if (
              role === Constants.AB_SYSTEM_ADMIN ||
              requiredUserLevelPermissions.every((required) => {
                return userLevelPermissions.includes(required);
              })
            ) {
              return <Component {...props} />;
            } else {
              throw new Error(Constants.ERROR_403);
            }
          } else if (allowedRoles.some((ar) => ar === role)) {
            return <Component {...props} />;
          } else {
            throw new Error(Constants.ERROR_403);
          }
        }

        return (
          <Redirect
            to={{
              pathname: "/signin",
              state: { from: props.location },
            }}
          />
        );
      }}
    />
  );
};

interface AppProps {
  isAuthenticated: boolean;
  user?: AuthUser;
  permissions?: Permission;
  getUserZipCode: (coords: { latitude: number; longitude: number }) => Promise<string>;
}

class App extends React.Component<AppProps> {
  public componentDidUpdate(prevProps: AppProps) {
    // As soon as the user authenticates, kick off geolocation in the background.
    // By the time they navigate to Nearby Stores the coordinates are cached in
    // localStorage, so the map centres on their real location immediately.
    if (!prevProps.isAuthenticated && this.props.isAuthenticated) {
      this.prefetchUserLocation();
    }
  }

  private prefetchUserLocation() {

    const cached = getItemJson("nearbyStoresData") as
      | { zipCode?: string; latitude?: number; longitude?: number }
      | undefined;
    if (cached?.latitude && cached?.longitude) return;

    if (!("geolocation" in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        this.props.getUserZipCode({ latitude, longitude }).then((zip) => {
          // Only write to cache once we have a real zip so NearbyStores never
          // reads an intermediate { zipCode: "" } entry that would block it
          // from fetching stores on its own componentDidMount.
          setItem("nearbyStoresData", { zipCode: zip || "", latitude, longitude });
        });
      },
      () => {
        // Geolocation unavailable or denied — NearbyStores will handle its own fallback.
      },
      { enableHighAccuracy: false }
    );
  }

  public renderRoutes() {
    const { isAuthenticated, user } = this.props;
    return (
      <>
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable={false}
          theme="light"
          transition={Bounce}
        />
        <Switch>
          {/* Admin Routes */}

          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
            ]}
            exact={true}
            path="/associated-chains"
            pageName="Associated Chains"
            component={(props: RouteProps) => (
              <Layout>
                <AssociatedChainsPage {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.VIEWER,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              Constants.VIEWER_NO_PSA_ACCESS
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/projects-new/:id/:roleId/review-feedback"
            pageName="Review Feedback"
            component={(props: RouteProps) => (
              <Layout>
                <ReviewFeedback {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.VIEWER_NO_PSA_ACCESS,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              Constants.VIEWER
            ]}
            path="/manage-all-data/projects/projects-new/:id/:roleId"
            pageName="Store Reset Status"
            component={(props: any) => (
              <Layout>
                <Project {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.VIEWER_NO_PSA_ACCESS,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects-new/:id/:roleId/store-reset-status/:projectId"
            pageName="Store Reset Status"
            component={(props: any) => (
              <Layout>
                <StoreResetStatus {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.VIEWER,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              Constants.VIEWER_NO_PSA_ACCESS
            ]}
            path="/manage-all-data/projects"
            pageName="Manage Projects"
            exact={true}
            allowProjectAdmin={true}
            component={(props: RouteProps) => (
              <Layout>
                <Projects {...props} />
              </Layout>
            )}
          />
          {/* New Permission Constants.INTERNAL_PROJECT_ADMIN,Constants.EXTERNAL_PROJECT_ADMIN */}
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            path="/create-project/:id"
            pageName="Create Project"
            allowProjectAdmin={true}
            component={(props: RouteProps) => (
              <Layout>
                <CreateProject {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            path="/create-new-project/:id"
            pageName="Create Project"
            allowProjectAdmin={true}
            component={(props: RouteProps) => (
              <Layout>
                <CreateProjectNew {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[Constants.AB_SYSTEM_ADMIN]}
            exact={true}
            path="/manage-all-data/manage-chains"
            pageName="Manage Chains"
            component={(props: RouteProps) => (
              <Layout>
                <ManageChains {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/manage-stores"
            pageName="Manage Stores"
            component={(props: RouteProps) => (
              <Layout>
                <ManageStores {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN
            ]}
            exact={true}
            path="/manage-all-data/manage-users"
            pageName="Manage Users"
            component={(props: RouteProps) => (
              <Layout>
                <ManageUsers {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/manage-mapping-data"
            pageName="Manage Mapping Data"
            component={(props: RouteProps) => (
              <Layout>
                <ManageMappingData {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[Constants.AB_SYSTEM_ADMIN]}
            exact={true}
            path="/manage-all-data/manage-chain-parent-data"
            pageName="Manage Chain Parent Data"
            component={(props: RouteProps) => (
              <Layout>
                <ManageChainParentData {...props} style={{ backgroundColor: "#FFFFFF" }} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            //TODO have to more roles to allow these page to them.
            allowedRoles={[Constants.AB_SYSTEM_ADMIN]}
            exact={true}
            path="/manage-all-data-old"
            pageName="Manage All Data"
            component={(props: RouteProps) => (
              <Layout>
                <ManageAllDataOld {...props} />
              </Layout>
            )}
          />

          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data"
            pageName="Manage All Data"
            component={(props: RouteProps) => (
              <Layout>
                <ManageAllData {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/projects-new/:id/:roleId/reports/:projectId"
            pageName="Manage Reports"
            component={(props: RouteProps) => (
              <Layout>
                <ManageReports {...props} />
              </Layout>
            )}
          />

          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/manage-files"
            pageName="Manage Files"
            component={(props: RouteProps) => (
              <Layout>
                <ManageFiles {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN,
              // Constants.AB_PROJECT_ADMIN,
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Constants.COMPETITOR_CSM,
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN
            ]}
            path="/surveys"
            pageName="Manage Surveys"
            component={(props: RouteProps) => (
              <Layout>
                <ManageSurveys {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/manage-files"
            pageName="Manage Files"
            component={(props: RouteProps) => (
              <Layout>
                <ManageFiles {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.CONTRIBUTER,
              Constants.VIEWER_NO_PSA_ACCESS,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              Constants.VIEWER,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/manage-files/:projectId"
            pageName="Manage Files"
            component={(props: RouteProps) => (
              <Layout>
                <ManageFiles {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.CONTRIBUTER,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/manage-psafiles/:projectId"
            pageName="Manage Files"
            component={(props: RouteProps) => (
              <Layout>
                <ManagePSAFiles {...props} />
              </Layout>
            )}
          />
          {/* Admin + CSM Routes */}
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_REGION_CAT_DIRECTOR,
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
            ]}
            path="/manage-all-data/surveys"
            pageName="Manage Surveys"
            component={(props: RouteProps) => (
              <Layout>
                <ManageSurveys {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects-new/surveys"
            pageName="Manage Surveys"
            component={(props: RouteProps) => (
              <Layout>
                <ManageSurveys {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects-new/announcements-new"
            pageName="Manage Announcements"
            allowProjectAdmin={true}
            component={(props: RouteProps) => (
              <Layout>
                <ManageAnnouncementsNew {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects-new/announcements/my-announcements"
            pageName="Manage Announcements"
            component={(props: RouteProps) => (
              <Layout>
                <ManageAnnouncementsNew {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              /* New Permission Constants.INTERNAL_PROJECT_ADMIN,Constants.EXTERNAL_PROJECT_ADMIN */
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            path="/announcements"
            pageName="Manage Announcements"
            allowProjectAdmin={true}
            component={(props: RouteProps) => (
              <Layout>
                <ManageAnnouncements {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              /* New Permission Constants.INTERNAL_PROJECT_ADMIN,Constants.EXTERNAL_PROJECT_ADMIN */
              Constants.EXTERNAL_PROJECT_ADMIN,
            ]}
            allowProjectAdmin={true}
            path="/announcements-new"
            pageName="Manage Announcements"
            component={(props: RouteProps) => (
              <Layout>
                <ManageAnnouncementsNew {...props} />
              </Layout>
            )}
          />
          {/* User Routes */}
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/"
            pageName="Root"
            component={() => {
              // redirect currently logged in user to their default route
              // if (user) {
              //   for (const { routes } of groups[user.role as UserRole]) {
              //     for (const route of [...routes]) {
              //       if (
              //         route.landingPageFor &&
              //         route.landingPageFor.some((lpr: string) => user && lpr === user.role)
              //       ) {
              //         return <Redirect to={{ pathname: route.route }} />;
              //       }
              //     }
              //   }
              // }
              // if no default route set, send user to associated chains page
              return <Redirect to={{ pathname: "/associated-chains" }} />;
            }}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN,
              // Constants.AB_PROJECT_ADMIN,
              // Constants.AB_INTERNAL_PROJECT_ADMIN,
              // Constants.AB_COLLABORATOR,
              // Constants.AB_MERCHANDISER,
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Constants.AB_WHOLESALER,
              // Constants.AB_VIEWER,
              // Constants.RETAILER,
              // Constants.VIEWER,
              // Constants.COMPETITOR_CSM,
              // Constants.COMPETITOR_WHOLESALER,
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
            ]}
            exact={true}
            path="/manage-all-data/starred"
            pageName="Home Page"
            component={(props: RouteProps) => (
              <Layout>
                <HomePage {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/starred/all"
            pageName="Starred Page"
            component={(props: RouteProps) => (
              <Layout>
                <StarredPage {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/projects/me"
            pageName="My Projects"
            component={(props: RouteProps) => (
              <Layout>
                <MyWork {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects/:projectId/stores/:storeId"
            pageName="Store Detail"
            component={(props: RouteProps) => (
              <Layout>
                <Store {...props} />
              </Layout>
            )}
          />
          {/* <Route
            // isAuthenticated={isAuthenticated}

            path="/projects/:projectId/stores/:storeId"
            pageName="Store Detail"
            component={(props: RouteProps) => (
              <Layout>
                <Store {...props} />
              </Layout>
            )}
          /> */}
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects-new/:projectId/stores/:storeId"
            pageName="Store Detail"
            component={(props: RouteProps) => (
              <Layout>
                <Store {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects/:id"
            pageName="Project Detail"
            component={(props: RouteProps) => (
              <Layout>
                {/* <ProjectOld {...props} /> */}
                <Project {...props} />
              </Layout>
            )}
          />
          {/* <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS
            ]}
            path="/projects-new/:id"
            pageName="Project Detail"
            component={(props: RouteProps) => (
              <Layout>
                <Project {...props} />
              </Layout>
            )}
          /> */}
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/downloads"
            pageName="Downloads"
            component={(props: RouteProps) => (
              <Layout>
                <Downloads {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/projects-new/:id/:roleId/downloads/:id"
            pageName="Downloads"
            component={(props: RouteProps) => (
              <Layout>
                <Downloads {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/downloads/:id"
            pageName="Downloads"
            component={(props: RouteProps) => (
              <Layout>
                <Downloads {...props} />
              </Layout>
            )}
          />

          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              //Constants.VIEWER,     //TODO: Verify access permissions for AB Viewer
            ]}
            requiredUserLevelPermissions={["access_DIR"]} //TODO: Verify access permissions for AB Viewer
            userLevelPermissions={user && user.user_level_permissions}
            exact={true}
            allowProjectAdmin={true}
            path="/iris-downloads"
            pageName="IRIS Downloads"
            component={(props: RouteProps) => (
              <Layout>
                <DirDownloads {...props} />
              </Layout>
            )}
          />

          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/stores/me"
            pageName="My Stores"
            component={(props: RouteProps) => (
              <Layout>
                <MyStores {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/my-feedback"
            pageName="My Feedback"
            component={(props: RouteProps) => (
              <Layout>
                <MyFeedback {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/projects-new/:id/:roleId/my-feedback/:projectId"
            pageName="My Feedback"
            component={(props: RouteProps) => (
              <Layout>
                <MyFeedback {...props} />
              </Layout>
            )}
          />
           <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects-new/:id/:roleId"
            pageName="Project Detail"
            component={(props: RouteProps) => (
              <Layout>
                <Project {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN,
              // Constants.AB_PROJECT_ADMIN,
              // Constants.AB_INTERNAL_PROJECT_ADMIN,
              // Constants.AB_COLLABORATOR,
              // Constants.AB_MERCHANDISER,
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Constants.AB_VIEWER,
              // Constants.RETAILER,
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/validation-completion"
            pageName="Validation Completion"
            component={(props: RouteProps) => (
              <Layout>
                <ValidationCompletion {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN,
              // Constants.AB_PROJECT_ADMIN,
              // Constants.AB_INTERNAL_PROJECT_ADMIN,
              // Constants.AB_COLLABORATOR,
              // Constants.AB_MERCHANDISER,
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Constants.AB_VIEWER,
              // Constants.RETAILER,
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/review-feedback"
            pageName="Review Feedback"
            component={(props: RouteProps) => (
              <Layout>
                <ReviewFeedback {...props} />
              </Layout>
            )}
          />
          {/* <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN,
              // Constants.AB_PROJECT_ADMIN,
              // Constants.AB_INTERNAL_PROJECT_ADMIN,
              // Constants.AB_COLLABORATOR,
              // Constants.AB_MERCHANDISER,
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Constants.AB_WHOLESALER,
              // Constants.RETAILER,
              // Constants.VIEWER,
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // New Access Assigned Constants.CONTRIBUTER
              Constants.CONTRIBUTER,
              // New Access Assigned Constants.CONTRIBUTER
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/projects-new/:id/:roleId/store-reset-status/:projectId"
            pageName="Store Reset Status"
            component={(props: any) => (
              <Layout>
                <StoreResetStatus {...props} />
              </Layout>
            )}
          /> */}
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            exact={true}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN,
              // Constants.AB_PROJECT_ADMIN,
              // Constants.AB_INTERNAL_PROJECT_ADMIN,
              // Constants.AB_COLLABORATOR,
              // Constants.AB_MERCHANDISER,
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Constants.AB_WHOLESALER,
              // Constants.RETAILER,
              // Constants.VIEWER,
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // New Access Assigned Constants.CONTRIBUTER
              Constants.CONTRIBUTER,
              // New Access Assigned Constants.CONTRIBUTER
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            path="/manage-all-data/store-reset-status"
            pageName="Store Reset Status"
            component={(props: any) => (
              <Layout>
                <StoreResetStatus {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN,
              // Constants.AB_PROJECT_ADMIN,
              // Constants.AB_INTERNAL_PROJECT_ADMIN,
              // Constants.AB_COLLABORATOR,
              // Constants.AB_MERCHANDISER,
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Constants.AB_VIEWER,
              // Constants.RETAILER,
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/manage-all-data/store-reset-feedback"
            pageName="Reset Feedback"
            component={(props: RouteProps) => (
              <Layout>
                <StoreResetFeedback {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // New Permission Assigned Constants.CONTRIBUTER_NO_PSA_ACCESS
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            allowProjectAdmin={true}
            path="/surveys/me"
            pageName="My Surveys"
            component={(props: RouteProps) => (
              <Layout>
                <MySurveys {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.RETAILER
              // Constants.RETAILER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/support"
            pageName="Support"
            component={(props: RouteProps) => (
              <Layout currentRoute="/support">
                <Support {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
              // Constants.AB_COLLABORATOR,
              Constants.EXTERNAL_PROJECT_ADMIN,
              // Commented Constants.AB_MERCHANDISER
              // Constants.AB_MERCHANDISER,
              // Commented Constants.AB_REGION_CAT_DIRECTOR
              // Constants.AB_REGION_CAT_DIRECTOR,
              // Commented Constants.AB_WHOLESALER
              // Constants.AB_WHOLESALER,
              // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
              // Constants.AB_VIEWER,
              Constants.VIEWER,
              // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
              // Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS,
              // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
              // Constants.COMPETITOR_CSM,
              Constants.CONTRIBUTER,
              // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
              // Constants.COMPETITOR_WHOLESALER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
            ]}
            exact={true}
            path="/announcements/my-announcements"
            pageName="My Announcements"
            component={(props: RouteProps) => (
              <Layout>
                <MyAnnouncements {...props} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS
            ]}
            exact={true}
            path="/manage-requests"
            pageName="Manage Requests"
            component={(props: RouteProps) => (
              <Layout>
                <ManageRequests />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
              Constants.EXTERNAL_PROJECT_ADMIN,
              Constants.CONTRIBUTER,
              Constants.CONTRIBUTER_NO_PSA_ACCESS,
              Constants.VIEWER,
              Constants.VIEWER_NO_PSA_ACCESS
            ]}
            exact={true}
            path="/manage-requests/requests-details/:requestId"
            pageName="Request Details"
            component={(props: RouteProps) => (
              <Layout>
                <RequestDetails />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              // Constants.AB_SYSTEM_ADMIN, Constants.AB_PROJECT_ADMIN
              Constants.AB_SYSTEM_ADMIN,
              // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
              // Constants.AB_PROJECT_ADMIN,
              Constants.INTERNAL_PROJECT_ADMIN,
            ]}
            exact={true}
            path="/manage-all-data/upload-attachments"
            pageName="Upload Attachments"
            component={(props: RouteProps) => (
              <Layout>
                <UploadAttachments {...props} user={user || undefined} />
              </Layout>
            )}
          />
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            role={user && (user.role as UserRole)}
            allowedRoles={[
              Constants.AB_SYSTEM_ADMIN,
            ]}
            exact={true}
            path="/manage-all-data/email-audit-log"
            pageName="Upload Attachments"
            component={(props: RouteProps) => (
              <Layout>
                <EmailAuditLogs />
              </Layout>
            )}
          />
          {hasFeature(Config.FEATURES, "nearbystores") && (
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              role={user && (user.role as UserRole)}
              allowedRoles={[
                Constants.AB_SYSTEM_ADMIN,
                // Commented Constants.AB_PROJECT_ADMIN changed to INTERNAL_PROJECT_ADMIN
                // Constants.AB_PROJECT_ADMIN,
                Constants.INTERNAL_PROJECT_ADMIN,
                // Commented Constants.AB_COLLABORATOR changed to Constants.EXTERNAL_PROJECT_ADMIN
                // Constants.AB_COLLABORATOR,
                Constants.EXTERNAL_PROJECT_ADMIN,
                // Commented Constants.AB_MERCHANDISER
                // Constants.AB_MERCHANDISER,
                // Commented Constants.AB_REGION_CAT_DIRECTOR
                // Constants.AB_REGION_CAT_DIRECTOR,
                // Commented Constants.AB_WHOLESALER
                // Constants.AB_WHOLESALER,
                // Commented Constants.AB_VIEWER changed to VIEWER Constants.VIEWER
                // Constants.AB_VIEWER,
                Constants.VIEWER,
                // Commented Constants.RETAILER
                // Constants.RETAILER,
                // Commented Constants.VIEWER changed to Constants.VIEWER_NO_PSA_ACCESS
                // Constants.VIEWER,
                Constants.VIEWER_NO_PSA_ACCESS,
                // Commented Constants.COMPETITOR_CSM changed to Constants.CONTRIBUTER
                // Constants.COMPETITOR_CSM,
                Constants.CONTRIBUTER,
                // Commented Constants.COMPETITOR_WHOLESALER changed to Constants.CONTRIBUTER_NO_PSA_ACCESS
                // Constants.COMPETITOR_WHOLESALER,
                Constants.CONTRIBUTER_NO_PSA_ACCESS,
              ]}
              exact={true}
              path="/nearby-stores"
              pageName="Nearby Stores"
              component={(props: RouteProps) => (
                <Layout>
                  <NearbyStores {...props} />
                </Layout>
              )}
            />
          )}
          {/* Public Routes */}
          <Route
            exact={true}
            path="/signin"
            pageName="Sign In"
            component={(props: RouteProps) => <SignIn {...props} />}
          />
          <Route
            exact={true}
            path="/logout"
            pageName="Sign Out"
            component={(props: RouteProps) => <Logout {...props} />}
          />
          {/* <Route
            exact={true}
            path="/associated-chains"
            pageName="Associated Chains"
            component={(props: RouteProps) => <AssociatedChainsPage />}
          /> */}
          <Route exact={true} path="/about-us" pageName="About Us" component={About} />

          <Route
            pageName="Error"
            component={() => {
              // route invalid paths to the 404 handler
              throw new Error(Constants.ERROR_404);
            }}
          />
        </Switch>
      </>
    );
  }

  public render() {
    const { user, permissions } = this.props;

    if (Config.GOOGLE_TAG_MANAGER_ID) {
      const gtmArgs = {
        gtmId: Config.GOOGLE_TAG_MANAGER_ID,
        dataLayer: {
          user: user,
        },
      };

      TagManager.initialize(gtmArgs);
    }
    const routes = this.renderRoutes();
    return (
      <Router>
        <ErrorBoundary>
          <PageContainer>
            {permissions ? (
              <PermissionsContext.Provider value={permissions}>
                {routes}
              </PermissionsContext.Provider>
            ) : (
              routes
            )}
          </PageContainer>
        </ErrorBoundary>
      </Router>
    );
  }
}

const mapStateToProps = (state: State) => {
  TagManager.dataLayer({
    dataLayer: {
      user: state.user,
    },
  });

  return {
    isAuthenticated: !!state.auth.content,
    user: state.auth_user.content,
    permissions: state.permissions.content,
  };
};

export default connect(mapStateToProps, { getUserZipCode })(App);
